/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { clusterById } from '@app/core/ngrx';
import { getClusterStatus, getClusterStatusText, getClusterStatusTooltip, isNebulaCluster } from '@app/domain/clusters';
import { Store } from '@ngrx/store';
import { ALL_CLUSTER_STATUSES, ClusterId, ClusterStatus } from '@shared/types/cluster';
import { NebulaClusterStatus } from '@shared/types/nebula';
import { identity, isEqual, isNil } from 'lodash-es';
import { match } from 'ts-pattern';
import { statusOutputBundle } from '../status-output/bundle';
import { StatusLevel } from '../status-output/status-output-param/status-output-param.component';

type ClusterStatusOutputStatus = ClusterStatus | NebulaClusterStatus.UPDATING;

type Status = { status: ClusterStatusOutputStatus; level: StatusLevel } & (
  | { icon: string; svgIcon?: undefined }
  | { icon?: undefined; svgIcon: string }
);

const ALL_STATUSES: ClusterStatusOutputStatus[] = [...ALL_CLUSTER_STATUSES, NebulaClusterStatus.UPDATING] as const;

@Component({
  selector: 'cluster-status',
  templateUrl: './cluster-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [statusOutputBundle, MatTooltipModule],
})
export class ClusterStatusComponent {
  /**
   * An alternative API for when you don't have a cluster info at hand
   */
  public inputStatus = input<ClusterStatus | undefined>(undefined, { alias: 'status' });
  public clusterId = input<ClusterId>();
  public onlyIcon = input(false, { transform: booleanAttribute, alias: 'onlyIcon' });

  private store = inject(Store);

  statuses: Status[] = ALL_STATUSES.map((status) =>
    match(status)
      .returnType<Status>()
      .with(ClusterStatus.ACTIVE, () => ({ status, level: StatusLevel.GREEN, icon: 'check_circle' }))
      .with(ClusterStatus.INACTIVE, () => ({ status, level: StatusLevel.YELLOW, icon: 'error' }))
      .with(ClusterStatus.DISCONNECTED, () => ({ status, level: StatusLevel.RED, icon: 'cancel' }))
      .with(ClusterStatus.PROVISIONING, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:more-circle' }))
      .with(ClusterStatus.RESUMING, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:more-circle' }))
      .with(ClusterStatus.DESTROYING, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:more-circle' }))
      .with(ClusterStatus.DESTROYED, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:destroying-circle' }))
      .with(ClusterStatus.FAILED, () => ({ status, level: StatusLevel.NEUTRAL, icon: 'cancel' }))
      .with(ClusterStatus.SUSPENDED, () => ({ status, level: StatusLevel.NEUTRAL, icon: 'pause_circle' }))
      .with(ClusterStatus.SUSPENDING, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:more-circle' }))
      .with(ClusterStatus.LIMITED, () => ({ status, level: StatusLevel.NEUTRAL, svgIcon: 'ggcc:speed-icon' }))
      .with(ClusterStatus.UNINITIALIZED, () => ({ status, level: StatusLevel.YELLOW, svgIcon: 'ggcc:more-circle' }))
      .with(NebulaClusterStatus.UPDATING, () => ({ status, level: StatusLevel.YELLOW, svgIcon: 'ggcc:more-circle' }))
      .exhaustive(),
  );

  private cluster = computed(() => {
    const id = this.clusterId();
    if (!id) return undefined;
    return this.store.selectSignal(clusterById(id), { equal: isEqual })();
  });

  private clusterInfoStatus = computed<ClusterStatusOutputStatus | null>(() => {
    const cluster = this.cluster();
    if (cluster?.nebula?.status === NebulaClusterStatus.UPDATING) return NebulaClusterStatus.UPDATING;
    return getClusterStatus(cluster);
  });
  public status = computed<ClusterStatusOutputStatus | null>(
    () => this.clusterInfoStatus() ?? this.inputStatus() ?? null,
  );
  getClusterStatusText = (status: ClusterStatusOutputStatus | null | undefined) => {
    if (status === NebulaClusterStatus.UPDATING) return 'Updating';
    return getClusterStatusText(status);
  };
  getClusterStatusTooltip = (status: ClusterStatusOutputStatus | null | undefined) => {
    if (status === NebulaClusterStatus.UPDATING) return;

    const cluster = this.cluster();
    const isNebula = !isNil(cluster) ? isNebulaCluster(cluster) : false;
    return getClusterStatusTooltip(status, isNebula);
  };

  // Silences the NG0956 warning (https://angular.dev/errors/NG0956)
  public identity = identity;
}
